/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from 'react';
import 'react-phone-input-2/lib/material.css';
import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  GlobalStyles,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box } from '@mui/system';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PhoneInput from 'react-phone-input-2';
import {
  PlanCheckoutV2Params,
  PlanSimulationResponse,
  PlanSimulationV2Params
} from 'src/models/register';
import useRegister from 'src/services/useRegister';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { useLocation } from 'react-router';
import {
  reducerUpdateEmail,
  reducerUpdatePrices,
  reducerUpdateSimulationPlanItems,
  reducerUpdateSimulationReferralCode
} from 'src/features/register';
import { getLogoByHostname } from 'src/utils/getLogoByHostname';
import { useLazyQuery, useMutation } from '@apollo/client';
import { POST_USER_PLAN_CHECKOUT_V2 } from 'src/services/graphql/register/mutation';
import { useAlert } from 'src/hooks/useAlert';
import Confirmation from 'src/components/Confirmation';
import { CustomGraphQLError } from 'src/models/general';
import { GET_PLAN_SIMULATION_V2 } from 'src/services/graphql/register/query';
import { Cancel } from '@mui/icons-material';

interface Props {
  period: any;
}

export interface RegisterAccount {
  fullName: string;
  email: string;
  phoneNumber: string;
  password: string;
  passwordConfirmation: string;
  referralCode: string;
}

function validationSchema() {
  return Yup.object({
    fullName: Yup.string().required(),
    email: Yup.string().email().required(),
    phoneNumber: Yup.string()
      .required('Phone number is required')
      .min(10, 'Phone number must be at least 10 digits'),
    password: Yup.string().required('Password is required'),
    passwordConfirmation: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirmation Password is required')
  });
}

const freeTrialTitle = 'Start Free Trial for 14 days';

const freeTrialSubtitle =
  'Create your account and explore all features free for 14 days.';

const FormAccount: FC<Props> = ({ period }) => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const { handleClickAlert } = useAlert();
  const isFreeTrial = params.get('isfreetrial') === 'true';
  const referalCodeUrl = params.get('referralcode');
  const { planSimulationV2, email, packagePlan } = useAppSelector(
    (state) => state.register
  );

  const { logo, width } = getLogoByHostname(true);
  const planCurrency = params.get('currency') ?? 'idr';

  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);

  const [referalCode, setReferalCode] = useState<string>(
    referalCodeUrl ? referalCodeUrl : ''
  );
  const [isReferalError, setIsReferalError] = useState<boolean>(false);
  const [isReferalSubmitted, setIsReferalSubmitted] = useState<boolean>(false);
  const [referalCodeErrorMessage, setReferalCodeErrorMessage] =
    useState<string>('');

  const [postCheckoutV2, { loading }] = useMutation<
    { billing_planCheckoutV2: { url: string } },
    PlanCheckoutV2Params
  >(POST_USER_PLAN_CHECKOUT_V2, {
    onCompleted: (data) => {
      if (data === null) {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Error submit data, please try again.',
          severity: 'error'
        });
        return;
      }
      window.location.href = data.billing_planCheckoutV2.url;
    },
    onError: () => {
      handleClickAlert({
        horizontal: 'center',
        vertical: 'top',
        message: 'Error submit data, please try again.',
        severity: 'error'
      });
    }
  });

  const { handleChange, handleSubmit, setSubmitting, errors, values, touched } =
    useFormik<RegisterAccount>({
      initialValues: {
        fullName: '',
        email: '',
        phoneNumber: '',
        password: '',
        passwordConfirmation: '',
        referralCode: ''
      },
      validationSchema: validationSchema(),
      onSubmit: () => {
        setOpenConfirmation(true);
      }
    });

  const [showPassword, setShowPassword] = useState([false, false]);
  const handleClickShowPassword = (index) => {
    setShowPassword((prev) =>
      prev.map((p, i) => {
        if (i === index) return !p;
        return p;
      })
    );
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const dispatch = useAppDispatch();

  const handleChangeEmail = (email: string) => {
    dispatch(reducerUpdateEmail(email));
  };

  const theme = useTheme();
  const isSmallOrLess = useMediaQuery(theme.breakpoints.down('sm'));

  const initCountryId =
    planCurrency === 'idr'
      ? '9cd88e16-510e-49f0-9ce8-f4d27f0b85bb'
      : '162021c9-becc-11eb-a7ce-1e87ea3f1b00';

  const [getPlanSimulationV2, { loading: planSimulationLoading }] =
    useLazyQuery<
      {
        billing_getPlanSimulationV2: PlanSimulationResponse;
      },
      PlanSimulationV2Params
    >(GET_PLAN_SIMULATION_V2, {
      onCompleted: (data) => {
        if (
          data.billing_getPlanSimulationV2.isReferralValid === false &&
          data.billing_getPlanSimulationV2.referralCode
        ) {
          setIsReferalError(true);
          setReferalCodeErrorMessage(
            data.billing_getPlanSimulationV2.referralErrorMessages[0]
          );
        } else {
          setIsReferalError(false);
          dispatch(
            reducerUpdateSimulationReferralCode(
              data.billing_getPlanSimulationV2.referralCode
            )
          );
          if (data.billing_getPlanSimulationV2.referralCode) {
            handleClickAlert({
              horizontal: 'center',
              vertical: 'top',
              message: 'Referral Code Applied',
              severity: 'success'
            });
          }
        }
        dispatch(
          reducerUpdateSimulationPlanItems(
            data.billing_getPlanSimulationV2.planItems
          )
        );
        dispatch(
          reducerUpdatePrices({
            discount: data?.billing_getPlanSimulationV2?.discount,
            referralDiscount:
              data?.billing_getPlanSimulationV2?.referralDiscount,
            total: data?.billing_getPlanSimulationV2?.total,
            subTotal: data?.billing_getPlanSimulationV2?.subTotal,
            promoType: data?.billing_getPlanSimulationV2?.promoType
          })
        );
      },
      onError: () => {
        setIsReferalError(true);
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Error fetching price, please try again.',
          severity: 'error'
        });
      },
      // Disable cache for this query to always get fresh data
      fetchPolicy: 'network-only'
    });

  const handleOk = async () => {
    const filteredPlanItems = planSimulationV2.planItems.filter(
      (plan) => plan.quota > 0
    );
    const isCustomPlan = packagePlan.name === 'custom';

    const payload: PlanCheckoutV2Params = {
      fullName: values.fullName,
      email: values.email,
      phoneNumber: values.phoneNumber,
      password: values.password,
      period: period.value,
      planItems: isCustomPlan ? filteredPlanItems : null,
      packagePlanId: !isCustomPlan ? packagePlan.id : null,
      ...(planSimulationV2.promoCodes.length > 0
        ? { promoCodes: planSimulationV2.promoCodes }
        : {}),
      ...(planSimulationV2.referralCode
        ? { referalCode: planSimulationV2.referralCode }
        : {})
    };
    setOpenConfirmation(false);
    const { errors } = await postCheckoutV2({ variables: payload });
    const gqlErrors = errors as CustomGraphQLError[];

    if (gqlErrors?.length) {
      const failures = gqlErrors[0]?.extensions?.reason?.failures;
      // show error user already registered
      handleClickAlert({
        horizontal: 'center',
        vertical: 'top',
        message: failures[0],
        severity: 'error'
      });
      return;
    }
    setSubmitting(false);
  };

  const handleUseReferral = (referral: string) => {
    const isCustomPlan = packagePlan.name === 'custom';

    setIsReferalSubmitted(true);
    getPlanSimulationV2({
      variables: {
        planItems: isCustomPlan ? planSimulationV2.planItems : null,
        countryId: initCountryId,
        period: period.value,
        promoCodes: planSimulationV2.promoCodes,
        email,
        referalCode: referral,
        packagePlanId: !isCustomPlan ? packagePlan.id : null
      }
    });
  };

  const handleResetReferralCode = () => {
    const isCustomPlan = packagePlan.name === 'custom';

    setReferalCode('');
    setIsReferalError(false);
    setIsReferalSubmitted(false);
    getPlanSimulationV2({
      variables: {
        planItems: isCustomPlan ? planSimulationV2.planItems : null,
        countryId: initCountryId,
        period: period.value,
        promoCodes: planSimulationV2.promoCodes,
        email,
        packagePlanId: !isCustomPlan ? packagePlan.id : null,
        referalCode: ''
      }
    });
  };

  useEffect(() => {
    if (referalCodeUrl) {
      dispatch(reducerUpdateSimulationReferralCode(referalCodeUrl));
    }
  }, [referalCodeUrl]);

  const ctaButtonText = () => {
    if (loading) {
      return isFreeTrial ? 'Loading...' : 'Checking out...';
    } else {
      return isFreeTrial ? 'Start My Free Trial' : 'Checkout';
    }
  };

  return (
    <Box
      sx={{
        width: '536px'
      }}
    >
      {/* Custom style for phone input */}
      <GlobalStyles
        styles={{
          '.react-tel-input .form-control:focus': {
            borderColor: '#064E66',
            boxShadow: '0 0 0 1px #064E66'
          }
        }}
      />
      <form onSubmit={handleSubmit} id="accountForm">
        <Grid container spacing={2}>
          <Grid item lg={12}>
            <img src={logo} alt={'company Logo'} width={width} />
          </Grid>
          <Grid item lg={12}>
            <Divider />
          </Grid>
          <Grid item lg={12}>
            <Typography variant="h1">
              {isFreeTrial ? freeTrialTitle : 'Start Your Journey'}
            </Typography>
            <Typography variant="caption">
              {isFreeTrial
                ? freeTrialSubtitle
                : 'Just one more step to start analyzing and monitoring your social media and online news to gain insights.'}
            </Typography>
          </Grid>
          <Grid item sm={12}>
            <FormControl fullWidth size="medium">
              <FormLabel>Full Name</FormLabel>
              <OutlinedInput
                name="fullName"
                onChange={handleChange}
                error={errors.fullName && touched.fullName}
                value={values.fullName}
              />
              <FormHelperText
                error
                variant="outlined"
                margin="dense"
                sx={{ ml: 0 }}
              >
                {errors.fullName && touched.fullName && errors.fullName}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sm={12}>
            <FormControl fullWidth size="medium">
              <FormLabel>Email Address</FormLabel>
              <OutlinedInput
                name="email"
                onChange={(e) => {
                  handleChange(e);
                  handleChangeEmail(e.target.value);
                }}
                error={errors.email && touched.email}
                value={values.email}
                autoComplete="new-password"
              />
              <FormHelperText
                error
                variant="outlined"
                margin="dense"
                sx={{ ml: 0 }}
              >
                {errors.email && touched.email && errors.email}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sm={12}>
            <FormControl
              fullWidth
              size="medium"
              error={!!(errors.phoneNumber && touched.phoneNumber)}
            >
              <FormLabel>Phone Number</FormLabel>
              <PhoneInput
                country={'id'}
                autoFormat={false}
                value={values.phoneNumber}
                specialLabel=""
                preferredCountries={['id', 'my']}
                onChange={(phone) => {
                  const event = {
                    target: {
                      name: 'phoneNumber',
                      value: phone
                    }
                  } as React.ChangeEvent<HTMLInputElement>;
                  handleChange(event);
                }}
                inputProps={{
                  name: 'phoneNumber',
                  autoComplete: 'tel'
                }}
                inputStyle={{
                  height: '40px',
                  width: '100%',
                  borderRadius: '8px'
                }}
              />
              <FormHelperText
                error
                variant="outlined"
                margin="dense"
                sx={{ ml: 0 }}
              >
                {errors.phoneNumber &&
                  touched.phoneNumber &&
                  errors.phoneNumber}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sm={12}>
            <FormControl fullWidth size="medium">
              <FormLabel>Password</FormLabel>
              <OutlinedInput
                name="password"
                onChange={handleChange}
                error={errors.password && touched.password}
                value={values.password}
                type={showPassword[0] ? 'text' : 'password'}
                autoComplete="new-password"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword(0)}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword[0] ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText
                error
                variant="outlined"
                margin="dense"
                sx={{ ml: 0 }}
              >
                {errors.password && touched.password && errors.password}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sm={12}>
            <FormControl fullWidth size="medium">
              <FormLabel>Confirm Password</FormLabel>
              <OutlinedInput
                name="passwordConfirmation"
                onChange={handleChange}
                error={
                  errors.passwordConfirmation && touched.passwordConfirmation
                }
                value={values.passwordConfirmation}
                type={showPassword[1] ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword(1)}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword[1] ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText
                error
                variant="outlined"
                margin="dense"
                sx={{ ml: 0 }}
              >
                {errors.passwordConfirmation &&
                  touched.passwordConfirmation &&
                  errors.passwordConfirmation}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sm={12}>
            <FormControl
              fullWidth
              size="medium"
              error={
                isReferalError && !planSimulationLoading && isReferalSubmitted
              }
            >
              <FormLabel>Referral Code (Optional)</FormLabel>
              <Stack direction="row" gap={0} width="100%">
                <OutlinedInput
                  name="referralCode"
                  sx={{
                    flexGrow: 1,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0
                  }}
                  onChange={(e) => {
                    setReferalCode(e.target.value);
                    if (isReferalSubmitted) {
                      setIsReferalSubmitted(false);
                    }
                  }}
                  value={referalCode}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear input"
                        onClick={handleResetReferralCode}
                        edge="end"
                      >
                        <Cancel fontSize="small" sx={{ color: '#D9D9D9' }} />
                      </IconButton>
                    </InputAdornment>
                  }
                />
                <Button
                  variant="contained"
                  disabled={!referalCode}
                  sx={{
                    borderRadius: 1,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                  onClick={() => handleUseReferral(referalCode)}
                >
                  Apply
                </Button>
              </Stack>
              <FormHelperText>
                {isReferalError &&
                !planSimulationLoading &&
                isReferalSubmitted ? (
                  referalCodeErrorMessage
                ) : (
                  <Typography fontSize="12px" color="#4B4B4B" marginTop={1}>
                    Get 5% off your first purchase by entering your colleague's
                    referral code (one-time use only).
                  </Typography>
                )}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
        <Box width={'100%'} textAlign="center">
          <Button
            variant="contained"
            color="primary"
            type="submit"
            size="large"
            sx={
              isSmallOrLess
                ? {
                    display: 'none'
                    // my: 4,
                    // px: 8,
                    // position: 'absolute',
                    // bottom: -680,
                    // width: 'calc(100% - 32px)'
                  }
                : {
                    my: 4,
                    px: 8,
                    width: '100%'
                  }
            }
            disabled={loading}
          >
            {ctaButtonText()}
          </Button>
        </Box>
      </form>
      <Confirmation
        onClose={() => setOpenConfirmation(false)}
        onOk={handleOk}
        open={openConfirmation}
        labelButton="Continue"
        title="Confirm Checkout"
        message="Please review your details before continuing and input the referral code if you have one, to get extra 5% off on your first purchase."
        loading={loading}
      />
    </Box>
  );
};

export default FormAccount;
